import { type Preference, PreferenceActions } from 'utils/preferences';
import {
  ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF,
  ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF_ALL_ENTITIES_KEY,
  ENTITY_TRANSACTION_EDIT_DRAWER_LAYOUT_PREF,
  TRANSACTIONS_TAB_COLUMNS_CONFIG_ALL_ENTITIES_KEY,
} from './constants';
import {
  normalizeTransactionEditDrawerColumnCount,
  type TransactionEditDrawerColumnCount,
} from './entity-transaction-helpers';

export type TransactionEditDrawerDensity = 'normal' | 'compact';

export type TransactionDrawerLineViewMode = 'form' | 'table';

export type TransactionEditDrawerLayoutPreference = {
  density: TransactionEditDrawerDensity;
  columnCount: TransactionEditDrawerColumnCount;
  lineViewMode: TransactionDrawerLineViewMode;
  /** Grid view: wrap long text in cells instead of truncating with ellipsis. */
  gridTextWrap?: boolean;
  /** Grid view: user-resized column widths (px) keyed by field id. */
  gridColumnWidths?: Record<string, number>;
  /**
   * Grid view: when true, column widths can be dragged/reset.
   * Default is locked (absent/false). Prefer this over the older
   * `gridColumnWidthsLocked` key so prior “unlocked by default” saves do not stick.
   */
  gridColumnWidthsUnlocked?: boolean;
};

export const DEFAULT_TRANSACTION_EDIT_DRAWER_LAYOUT: TransactionEditDrawerLayoutPreference =
  {
    density: 'normal',
    columnCount: 4,
    lineViewMode: 'form',
    gridTextWrap: false,
    gridColumnWidths: undefined,
    gridColumnWidthsUnlocked: false,
  };

function normalizeGridColumnWidths(
  raw: unknown
): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const width = Math.round(value);
    if (width < 32 || width > 640) continue;
    if (!key.trim()) continue;
    out[key] = width;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizeTransactionEditDrawerDensity(
  raw: unknown
): TransactionEditDrawerDensity {
  return raw === 'compact' ? 'compact' : 'normal';
}

function normalizeTransactionDrawerLineViewMode(
  raw: unknown
): TransactionDrawerLineViewMode {
  return raw === 'table' ? 'table' : 'form';
}

export function normalizeTransactionEditDrawerLayoutPreference(
  raw: unknown
): TransactionEditDrawerLayoutPreference {
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_TRANSACTION_EDIT_DRAWER_LAYOUT;
  }
  const value = raw as Record<string, unknown>;
  return {
    density: normalizeTransactionEditDrawerDensity(value.density),
    columnCount: normalizeTransactionEditDrawerColumnCount(
      value.columnCount ?? DEFAULT_TRANSACTION_EDIT_DRAWER_LAYOUT.columnCount
    ),
    lineViewMode: normalizeTransactionDrawerLineViewMode(
      value.lineViewMode ?? DEFAULT_TRANSACTION_EDIT_DRAWER_LAYOUT.lineViewMode
    ),
    gridTextWrap: value.gridTextWrap === true,
    gridColumnWidths: normalizeGridColumnWidths(value.gridColumnWidths),
    // Opt-in unlock only — default locked for new and older prefs.
    gridColumnWidthsUnlocked: value.gridColumnWidthsUnlocked === true,
  };
}

export function resolveTransactionEditDrawerLayoutFromPreferences(
  handlePreferences: (
    action: PreferenceActions,
    preference: Preference
  ) => unknown
): TransactionEditDrawerLayoutPreference {
  const raw = handlePreferences(PreferenceActions.GET, {
    name: ENTITY_TRANSACTION_EDIT_DRAWER_LAYOUT_PREF,
  });
  return normalizeTransactionEditDrawerLayoutPreference(raw);
}

export function transactionsTabColumnsConfigKeyForOrg(
  organisationId: string
): string {
  return `transactions-tab-columns-config-${organisationId}`;
}

export type TransactionsTabColumnsPersistConfig = {
  visibleColumns: string[];
  lockedColumns: string[];
  columnOrder: string[];
  /** Stored with column prefs so one SET persists both (avoids race losing the switch). */
  editInDrawer?: boolean;
};

function readEditInDrawerFromColumnsConfig(
  config: TransactionsTabColumnsPersistConfig | undefined
): boolean | undefined {
  if (config?.editInDrawer === true || config?.editInDrawer === false) {
    return config.editInDrawer === true;
  }
  return undefined;
}

export function resolveEditInDrawerFromPreferences(
  handlePreferences: (
    action: PreferenceActions,
    preference: Preference
  ) => unknown,
  organisationId: string
): boolean {
  if (!organisationId?.trim()) return false;

  const entityColumns = handlePreferences(PreferenceActions.GET, {
    name: transactionsTabColumnsConfigKeyForOrg(organisationId),
  }) as TransactionsTabColumnsPersistConfig | undefined;
  const fromEntityColumns = readEditInDrawerFromColumnsConfig(entityColumns);
  if (fromEntityColumns !== undefined) {
    return fromEntityColumns;
  }

  const globalColumns = handlePreferences(PreferenceActions.GET, {
    name: TRANSACTIONS_TAB_COLUMNS_CONFIG_ALL_ENTITIES_KEY,
  }) as TransactionsTabColumnsPersistConfig | undefined;
  const fromGlobalColumns = readEditInDrawerFromColumnsConfig(globalColumns);
  if (fromGlobalColumns !== undefined) {
    return fromGlobalColumns;
  }

  const orgName = `${ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF}-${organisationId}`;
  const orgRaw = handlePreferences(PreferenceActions.GET, { name: orgName });
  if (orgRaw === true || orgRaw === false) {
    return orgRaw === true;
  }
  const allRaw = handlePreferences(PreferenceActions.GET, {
    name: ENTITY_TRANSACTIONS_EDIT_IN_DRAWER_PREF_ALL_ENTITIES_KEY,
  });
  if (allRaw === true || allRaw === false) {
    return allRaw === true;
  }
  // Default: open drawer unless user saved otherwise.
  return true;
}

/** Column keys only — omit `editInDrawer` when applying to table state. */
export function toTransactionsTabColumnsState(
  config: TransactionsTabColumnsPersistConfig
): Pick<
  TransactionsTabColumnsPersistConfig,
  'visibleColumns' | 'lockedColumns' | 'columnOrder'
> {
  const { visibleColumns, lockedColumns, columnOrder } = config;
  return { visibleColumns, lockedColumns, columnOrder };
}

export function resolveTransactionsTabColumnsFromPreferences(
  handlePreferences: (
    action: PreferenceActions,
    preference: Preference
  ) => unknown,
  organisationId: string
): TransactionsTabColumnsPersistConfig | null {
  if (!organisationId?.trim()) return null;
  const entity = handlePreferences(PreferenceActions.GET, {
    name: transactionsTabColumnsConfigKeyForOrg(organisationId),
  }) as TransactionsTabColumnsPersistConfig | undefined;
  if (entity?.visibleColumns?.length) {
    return entity;
  }
  const globalAll = handlePreferences(PreferenceActions.GET, {
    name: TRANSACTIONS_TAB_COLUMNS_CONFIG_ALL_ENTITIES_KEY,
  }) as TransactionsTabColumnsPersistConfig | undefined;
  if (globalAll?.visibleColumns?.length) {
    return globalAll;
  }
  return null;
}
